import { useState, useRef } from 'react';
import { GridState } from '../../types';

interface DragState {
  isDragging: boolean;
  nodeId: string | null;
  edgeId: string | null;
  dragType: 'position' | 'rating' | 'pan';
  startX: number;
  startY: number;
  nodeStartX: number;
  nodeStartY: number;
  nodeStartValue: number;
  edgeStartRating: number;
  panStartX: number;
  panStartY: number;
}

interface ViewTransform {
  x: number;
  y: number;
  scale: number;
}

interface UseDragProps {
  gridState: GridState;
  viewTransform: ViewTransform;
  setViewTransform: (transform: ViewTransform) => void;
  svgRef: React.RefObject<SVGSVGElement>;
  onNodePositionChange: (nodeId: string, x: number, y: number) => void;
  onEdgeRatingChange: (edgeId: string, rating: number) => void;
  // Called when a node/edge drag gesture finishes, so callers can finalize
  // (e.g. seal an undo/redo coalescing group).
  onDragEnd?: () => void;
}

export function useDrag({
  gridState,
  viewTransform,
  setViewTransform,
  svgRef,
  onNodePositionChange,
  onEdgeRatingChange,
  onDragEnd,
}: UseDragProps) {
  const { nodes, edges } = gridState;
  
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    nodeId: null,
    edgeId: null,
    dragType: 'position',
    startX: 0,
    startY: 0,
    nodeStartX: 0,
    nodeStartY: 0,
    nodeStartValue: 0,
    edgeStartRating: 0,
    panStartX: 0,
    panStartY: 0,
  });

  const getMousePosition = (event: React.MouseEvent<SVGSVGElement | SVGGElement>) => {
    if (!svgRef.current) return { x: 0, y: 0 };
    
    // Use SVG's built-in coordinate system conversion
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = event.clientX;
    pt.y = event.clientY;
    
    // Transform through all matrix transformations to get SVG coordinates
    const screenCTM = svg.getScreenCTM();
    if (!screenCTM) return { x: 0, y: 0 };
    
    const svgPoint = pt.matrixTransform(screenCTM.inverse());
    
    // Apply inverse of view transform to get world coordinates  
    return {
      x: (svgPoint.x - viewTransform.x) / viewTransform.scale,
      y: (svgPoint.y - viewTransform.y) / viewTransform.scale,
    };
  };

  const getRawMousePosition = (event: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current) return { x: 0, y: 0 };
    
    // Use SVG's built-in coordinate system conversion for raw position
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = event.clientX;
    pt.y = event.clientY;
    
    const screenCTM = svg.getScreenCTM();
    if (!screenCTM) return { x: 0, y: 0 };
    
    const svgPoint = pt.matrixTransform(screenCTM.inverse());
    
    return {
      x: svgPoint.x,
      y: svgPoint.y,
    };
  };

  const handleNodeMouseDown = (event: React.MouseEvent<SVGGElement>, nodeId: string) => {
    event.preventDefault();
    
    const mousePos = getMousePosition(event);
    const node = nodes.find(n => n.id === nodeId);
    
    if (!node) return;

    // Always drag position (value dragging removed)
    const dragType = 'position';

    setDragState({
      isDragging: true,
      nodeId,
      edgeId: null,
      dragType,
      startX: mousePos.x,
      startY: mousePos.y,
      nodeStartX: node.x,
      nodeStartY: node.y,
      nodeStartValue: node.value,
      edgeStartRating: 0,
      panStartX: 0,
      panStartY: 0,
    });
  };

  const handleEdgeMouseDown = (event: React.MouseEvent<SVGGElement>, edgeId: string) => {
    if (!event.shiftKey) return; // Only allow shift+drag for edges
    
    event.preventDefault();
    event.stopPropagation();
    
    const mousePos = getMousePosition(event);
    const edge = edges.find(e => e.id === edgeId);
    
    if (!edge) return;

    setDragState({
      isDragging: true,
      nodeId: null,
      edgeId,
      dragType: 'rating',
      startX: mousePos.x,
      startY: mousePos.y,
      nodeStartX: 0,
      nodeStartY: 0,
      nodeStartValue: 0,
      edgeStartRating: edge.rating,
      panStartX: 0,
      panStartY: 0,
    });
  };

  const handleMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
    if (!dragState.isDragging) return;
    
    const mousePos = getMousePosition(event);
    
    if (dragState.dragType === 'position') {
      // Place node directly under mouse cursor
      if (dragState.nodeId) {
        onNodePositionChange(dragState.nodeId, mousePos.x, mousePos.y);
      }
    // Value dragging removed - values are now computed from dispatch
    } else if (dragState.dragType === 'rating' && dragState.edgeId) {
      const deltaY = mousePos.y - dragState.startY;
      
      // Sensitivity for rating adjustment (negative because dragging up should increase rating)
      const ratingDelta = -deltaY * 0.1; // 0.1 MW per pixel
      
      let newRating = dragState.edgeStartRating + ratingDelta;
      
      // Clamp rating between 1 and 500 MW
      newRating = Math.max(1, Math.min(500, newRating));
      
      onEdgeRatingChange(dragState.edgeId, newRating);
    } else if (dragState.dragType === 'pan') {
      const rawMousePos = getRawMousePosition(event);
      const deltaX = rawMousePos.x - dragState.startX;
      const deltaY = rawMousePos.y - dragState.startY;
      
      setViewTransform({
        ...viewTransform,
        x: dragState.panStartX + deltaX,
        y: dragState.panStartY + deltaY,
      });
    }
  };

  const handleMouseUp = () => {
    // Finalize a completed node reposition or edge-rating drag so the next
    // gesture on the same element is a separate undo step.
    if (dragState.isDragging && (dragState.dragType === 'position' || dragState.dragType === 'rating')) {
      onDragEnd?.();
    }
    setDragState({
      isDragging: false,
      nodeId: null,
      edgeId: null,
      dragType: 'position',
      startX: 0,
      startY: 0,
      nodeStartX: 0,
      nodeStartY: 0,
      nodeStartValue: 0,
      edgeStartRating: 0,
      panStartX: 0,
      panStartY: 0,
    });
  };

  const handleSVGMouseDown = (event: React.MouseEvent<SVGSVGElement>) => {
    // Only start panning if clicking on SVG background (not on interactive elements)
    const target = event.target as Element;
    const isBackground = target === svgRef.current || 
                        target.id === 'background' ||
                        target.tagName === 'svg' ||
                        (target.tagName === 'rect' && target.getAttribute('fill') === 'url(#grid)');
    
    if (isBackground) {
      const rawMousePos = getRawMousePosition(event);
      
      setDragState({
        isDragging: true,
        nodeId: null,
        edgeId: null,
        dragType: 'pan',
        startX: rawMousePos.x,
        startY: rawMousePos.y,
        nodeStartX: 0,
        nodeStartY: 0,
        nodeStartValue: 0,
        edgeStartRating: 0,
        panStartX: viewTransform.x,
        panStartY: viewTransform.y,
      });
    }
  };

  return {
    dragState,
    handleNodeMouseDown,
    handleEdgeMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleSVGMouseDown,
    getMousePosition,
  };
}
